export type EmailBodyInlineNode =
  | { type: 'text'; value: string }
  | { type: 'link'; href: string; label: string }
  | { type: 'strong'; children: EmailBodyInlineNode[] }
  | { type: 'em'; children: EmailBodyInlineNode[] }
  | { type: 'image-ref'; name: string };

export type EmailBodyBlock =
  | { type: 'paragraph'; lines: EmailBodyInlineNode[][] }
  | { type: 'quote'; lines: EmailBodyInlineNode[][] }
  | { type: 'quote-header'; text: string };

export interface ParseEmailBodyOptions {
  attachmentNames?: string[];
}

const QUOTE_HEADER_RE = /^On .+(?:wrote|sent):\s*$/i;
const IMAGE_PLACEHOLDER_RE = /\[image:\s*([^\]]+)\]/gi;

function normalizeNewlines(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function attachmentNameMatches(candidate: string, attachmentNames: string[]): boolean {
  const needle = candidate.trim().toLowerCase();
  if (!needle) return false;
  return attachmentNames.some((name) => {
    const hay = name.trim().toLowerCase();
    return hay === needle || hay.endsWith(`/${needle}`) || hay.endsWith(`\\${needle}`);
  });
}

function stripMatchedImagePlaceholders(text: string, attachmentNames?: string[]): string {
  if (!attachmentNames?.length) return text;
  return text.replace(IMAGE_PLACEHOLDER_RE, (match, rawName: string) =>
    attachmentNameMatches(rawName, attachmentNames) ? '' : match,
  );
}

function isQuoteLine(line: string): boolean {
  return /^>+\s?/.test(line);
}

function stripQuotePrefix(line: string): string {
  return line.replace(/^>+\s?/, '');
}

export function parseInlineEmailContent(line: string): EmailBodyInlineNode[] {
  const nodes: EmailBodyInlineNode[] = [];
  let index = 0;

  while (index < line.length) {
    const rest = line.slice(index);

    const linkMatch = rest.match(/^(?:<((?:https?:\/\/)[^>]+)>|((?:https?:\/\/)[^\s<]+))/);
    if (linkMatch) {
      const href = (linkMatch[1] ?? linkMatch[2] ?? '').trim();
      nodes.push({
        type: 'link',
        href,
        label: href.replace(/^https?:\/\//, ''),
      });
      index += linkMatch[0].length;
      continue;
    }

    const boldMatch = rest.match(/^\*([^*\n]+)\*/);
    if (boldMatch) {
      nodes.push({
        type: 'strong',
        children: [{ type: 'text', value: boldMatch[1] ?? '' }],
      });
      index += boldMatch[0].length;
      continue;
    }

    const emMatch = rest.match(/^_([^_\n]+)_/);
    if (emMatch) {
      nodes.push({
        type: 'em',
        children: [{ type: 'text', value: emMatch[1] ?? '' }],
      });
      index += emMatch[0].length;
      continue;
    }

    const imageMatch = rest.match(/^\[image:\s*([^\]]+)\]/i);
    if (imageMatch) {
      nodes.push({ type: 'image-ref', name: (imageMatch[1] ?? '').trim() });
      index += imageMatch[0].length;
      continue;
    }

    const nextSpecial = rest.search(/[<_*\[]|https?:\/\//);
    if (nextSpecial === -1) {
      if (rest) nodes.push({ type: 'text', value: rest });
      break;
    }
    if (nextSpecial > 0) {
      nodes.push({ type: 'text', value: rest.slice(0, nextSpecial) });
      index += nextSpecial;
      continue;
    }

    nodes.push({ type: 'text', value: rest[0] ?? '' });
    index += 1;
  }

  return nodes;
}

function pushParagraphBlock(blocks: EmailBodyBlock[], lines: EmailBodyInlineNode[][]) {
  if (!lines.length) return;
  blocks.push({ type: 'paragraph', lines });
}

function pushQuoteBlock(blocks: EmailBodyBlock[], lines: EmailBodyInlineNode[][]) {
  if (!lines.length) return;
  blocks.push({ type: 'quote', lines });
}

export function parseEmailBody(text: string, options?: ParseEmailBodyOptions): EmailBodyBlock[] {
  const normalized = stripMatchedImagePlaceholders(normalizeNewlines(text), options?.attachmentNames);
  const rawLines = normalized.split('\n');
  const blocks: EmailBodyBlock[] = [];

  let paragraphLines: EmailBodyInlineNode[][] = [];
  let quoteLines: EmailBodyInlineNode[][] = [];

  const flushParagraph = () => {
    pushParagraphBlock(blocks, paragraphLines);
    paragraphLines = [];
  };

  const flushQuote = () => {
    pushQuoteBlock(blocks, quoteLines);
    quoteLines = [];
  };

  for (const rawLine of rawLines) {
    const line = rawLine.trimEnd();

    if (!line.trim()) {
      flushQuote();
      flushParagraph();
      continue;
    }

    if (QUOTE_HEADER_RE.test(line.trim())) {
      flushQuote();
      flushParagraph();
      blocks.push({ type: 'quote-header', text: line.trim() });
      continue;
    }

    if (isQuoteLine(line)) {
      flushParagraph();
      quoteLines.push(parseInlineEmailContent(stripQuotePrefix(line)));
      continue;
    }

    flushQuote();
    paragraphLines.push(parseInlineEmailContent(line));
  }

  flushQuote();
  flushParagraph();
  return blocks;
}

export function emailBodyToPlainText(text: string, options?: ParseEmailBodyOptions): string {
  const blocks = parseEmailBody(text, options);
  const chunks: string[] = [];

  for (const block of blocks) {
    if (block.type === 'quote-header') {
      chunks.push(block.text);
      continue;
    }
    for (const line of block.lines) {
      const lineText = line
        .map((node) => {
          if (node.type === 'text') return node.value;
          if (node.type === 'link') return node.label;
          if (node.type === 'image-ref') return node.name;
          if (node.type === 'strong' || node.type === 'em') {
            return node.children.map((child) => (child.type === 'text' ? child.value : '')).join('');
          }
          return '';
        })
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
      if (lineText) chunks.push(lineText);
    }
  }

  return chunks.join(' ').replace(/\s+/g, ' ').trim();
}
